/**
 * Suppliers Tab - Manage Suppliers
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Checkbox,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';

import {
  getSuppliers,
  createSupplier,
  updateSupplier,
  bulkDeleteSuppliers,
  Supplier,
  SupplierCreate,
} from '../../../services/supplierDeclarationService';

const SuppliersTab: React.FC = () => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState<SupplierCreate>({
    name: '',
    email: '',
    contact_person: '',
    phone: '',
    country: '',
  });

  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadSuppliers();
  }, []);

  const loadSuppliers = async () => {
    setLoading(true);
    try {
      const data = await getSuppliers();
      setSuppliers(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingSupplier) {
        await updateSupplier(editingSupplier.id, formData);
        toast.success('Supplier updated successfully');
      } else {
        await createSupplier(formData);
        toast.success('Supplier created successfully');
      }
      setDialogOpen(false);
      setEditingSupplier(null);
      setFormData({ name: '', email: '', contact_person: '', phone: '', country: '' });
      loadSuppliers();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to save supplier');
    }
  };

  const handleEditClick = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name,
      email: supplier.email,
      contact_person: supplier.contact_person || '',
      phone: supplier.phone || '',
      country: supplier.country || '',
    });
    setDialogOpen(true);
  };

  // Selection handlers
  const isAllSelected = suppliers.length > 0 && selectedIds.size === suppliers.length;
  const isIndeterminate = selectedIds.size > 0 && selectedIds.size < suppliers.length;

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(suppliers.map((s: Supplier) => s.id)));
    }
  };

  const handleSelectOne = (id: string) => {
    setSelectedIds((prev: Set<string>) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    setDeleting(true);
    try {
      const result = await bulkDeleteSuppliers(Array.from(selectedIds));
      toast.success(`${result.deleted_count} supplier(s) and all related declarations deleted`);
      setSelectedIds(new Set());
      setDeleteConfirmOpen(false);
      loadSuppliers();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to delete suppliers');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Box />
        <Box sx={{ display: 'flex', gap: 1 }}>
          {selectedIds.size > 0 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteConfirmOpen(true)}
            >
              Delete ({selectedIds.size})
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Add Supplier
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Contact Person</TableCell>
              <TableCell>Country</TableCell>
              <TableCell align="right">Declarations</TableCell>
              <TableCell align="right">Active</TableCell>
              <TableCell align="right">Expiring</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {suppliers.map((supplier: Supplier) => (
              <TableRow
                key={supplier.id}
                selected={selectedIds.has(supplier.id)}
                sx={{ '&.Mui-selected': { backgroundColor: 'action.selected' } }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedIds.has(supplier.id)}
                    onChange={() => handleSelectOne(supplier.id)}
                  />
                </TableCell>
                <TableCell>{supplier.name}</TableCell>
                <TableCell>{supplier.email}</TableCell>
                <TableCell>{supplier.contact_person || '-'}</TableCell>
                <TableCell>{supplier.country || '-'}</TableCell>
                <TableCell align="right">{supplier.declaration_count}</TableCell>
                <TableCell align="right">{supplier.active_declarations}</TableCell>
                <TableCell align="right">{supplier.expiring_soon}</TableCell>
                <TableCell>
                  <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => handleEditClick(supplier)}>
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {suppliers.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No suppliers found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Suppliers</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedIds.size} supplier(s)?
            This will also permanently delete all their declarations, alerts, and email logs.
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDeleteSelected} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : `Delete ${selectedIds.size} Supplier(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add / Edit Supplier Dialog */}
      <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); setEditingSupplier(null); setFormData({ name: '', email: '', contact_person: '', phone: '', country: '' }); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Supplier Name"
                value={formData.name}
                onChange={(e: any) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e: any) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Person"
                value={formData.contact_person}
                onChange={(e: any) => setFormData({ ...formData, contact_person: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e: any) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Country"
                value={formData.country}
                onChange={(e: any) => setFormData({ ...formData, country: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialogOpen(false); setEditingSupplier(null); setFormData({ name: '', email: '', contact_person: '', phone: '', country: '' }); }}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!formData.name || !formData.email}>
            {editingSupplier ? 'Save Changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuppliersTab;
